import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Car, DollarSign, CheckCircle2, AlertTriangle, XCircle, Loader2,
  ArrowRight, ArrowLeft, Mail, Phone, User, Camera, Sparkles,
  Crown, Lock, Zap, Fuel, Ship, Flag, Package, Download,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import PayPalButton from "./PayPalButton";
import BuyingPowerGauge from "./BuyingPowerGauge";
import { CarTermSlider } from "./CarTermSlider";
import { bankRowsAtTerm } from "../lib/carLoan";
import { downloadCarReportPdf } from "../lib/carReportPdf";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const CAR_SITES = [
  { name: "eMoters", url: "https://emoters.com" },
  { name: "Sanpin Bahamas", url: "https://www.sanpinbahamas.com" },
  { name: "Bahamas Friendly Ford", url: "https://bahamasfriendlyford.com" },
  { name: "Easy Car Sales", url: "https://easycarsales.com" },
];

export default function CarBuyingPowerFlow({ onBack }) {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [ocrScanning, setOcrScanning] = useState(false);
  const [scrapeError, setScrapeError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [unlockingReport, setUnlockingReport] = useState(false);
  const [proReport, setProReport] = useState(null);

  const [listing, setListing] = useState({ price: "", title: "", image: "" });
  const [manual, setManual] = useState(false);

  // Vehicle details collected on step 2
  const [vehicle, setVehicle] = useState({
    condition: "new",      // "new" | "used"
    fuel_type: "gas",      // "gas" | "hybrid" | "electric"
    imported: false,       // local vs imported
  });

  // Finances on step 3
  const [fin, setFin] = useState({
    annual_income: "", monthly_debts: "", down_payment: "",
    employment_status: "employed",
  });

  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [promo, setPromo] = useState({ code: "", applying: false, result: null, error: "" });

  const totalSteps = 4;
  const pct = (step / totalSteps) * 100;

  // Auto-scroll on step change
  useEffect(() => {
    const map = { 1: "car-step-1", 2: "car-step-2", 3: "car-step-3", 4: "car-step-4" };
    const el = document.querySelector(`[data-testid='${map[step]}']`);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [step]);

  useEffect(() => {
    if (showPayment || proReport) {
      const el = document.querySelector("[data-testid='car-paywall-section'], [data-testid='car-pro-report']");
      if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [showPayment, proReport]);

  const importFromScreenshot = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please pick an image file.");
    if (file.size > 6 * 1024 * 1024) return toast.error("Image too large (max 6MB).");
    setOcrScanning(true);
    setScrapeError(null);
    const previewUrl = URL.createObjectURL(file);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/api/buying-power/car/scan-listing`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.price) {
        setListing({
          price: res.data.price,
          title: res.data.title || "",
          image: previewUrl,
        });
        setManual(true);
        toast.success(`Detected ${fmt(res.data.price)} from your screenshot.`);
      } else {
        setListing((l) => ({ ...l, image: previewUrl }));
        setScrapeError("Couldn't read a price from that image. Please enter it manually below.");
        setManual(true);
      }
    } catch (e) {
      setScrapeError(e?.response?.data?.detail || "Screenshot scan failed. Enter price manually below.");
      setManual(true);
    } finally {
      setOcrScanning(false);
    }
  };

  const calculate = async () => {
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/api/buying-power/car/calculate`, {
        listing_price: parseFloat(listing.price) || null,
        annual_income: parseFloat(fin.annual_income),
        monthly_debts: parseFloat(fin.monthly_debts) || 0,
        down_payment: parseFloat(fin.down_payment) || 0,
        condition: vehicle.condition,
        fuel_type: vehicle.fuel_type,
        imported: vehicle.imported,
        employment_status: fin.employment_status,
      });
      setResult(res.data);
      setStep(4);
      await saveLead(res.data);
      try {
        const st = await axios.post(`${API}/api/buying-power/car/paid-status`, { email: contact.email });
        if (st.data.paid && st.data.transaction_id && st.data.transaction_id !== "FREE") {
          await fetchProReport(st.data.transaction_id);
        }
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const applyPromo = async () => {
    if (!promo.code.trim()) return;
    setPromo((p) => ({ ...p, applying: true, error: "", result: null }));
    try {
      const res = await axios.post(`${API}/api/promo/validate`, {
        code: promo.code.trim(),
        base_price: 29.99,
      });
      setPromo((p) => ({ ...p, applying: false, result: res.data, error: "" }));
      if (res.data.is_free) toast.success("Promo applied — your report is 100% free!");
      else toast.success(`Promo applied — $${res.data.discount_amount.toFixed(2)} off`);
    } catch (e) {
      setPromo((p) => ({ ...p, applying: false, result: null, error: e?.response?.data?.detail || "Invalid promo code" }));
    }
  };

  const saveLead = async (r = result) => {
    try {
      await axios.post(`${API}/api/buying-power/car/lead`, {
        name: contact.name, email: contact.email, phone: contact.phone,
        listing_price: parseFloat(listing.price) || null,
        listing_title: listing.title,
        condition: vehicle.condition,
        fuel_type: vehicle.fuel_type,
        imported: vehicle.imported,
        max_car_price_low: r.max_car_price_low,
        max_car_price_high: r.max_car_price_high,
        match_status: r.match_status,
      });
    } catch { /* non-fatal */ }
  };

  const unlockWithPromo = async () => {
    setUnlockingReport(true);
    try {
      const res = await axios.post(`${API}/api/buying-power/car/pro-report`, {
        transaction_id: "FREE",
        promo_code: promo.code.trim(),
        name: contact.name, email: contact.email, phone: contact.phone,
        listing_price: parseFloat(listing.price) || null,
        listing_title: listing.title,
        annual_income: parseFloat(fin.annual_income),
        monthly_debts: parseFloat(fin.monthly_debts) || 0,
        down_payment: parseFloat(fin.down_payment) || 0,
        condition: vehicle.condition,
        fuel_type: vehicle.fuel_type,
        imported: vehicle.imported,
        employment_status: fin.employment_status,
      });
      setProReport(res.data);
      toast.success("Premium car report unlocked with promo!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promo unlock failed");
    } finally {
      setUnlockingReport(false);
    }
  };

  const startCheckout = async () => {
    setGateSubmitting(true);
    setShowPayment(true);
    setGateSubmitting(false);
  };

  const fetchProReport = async (transaction_id) => {
    setUnlockingReport(true);
    try {
      const res = await axios.post(`${API}/api/buying-power/car/pro-report`, {
        transaction_id,
        name: contact.name, email: contact.email, phone: contact.phone,
        listing_price: parseFloat(listing.price) || null,
        listing_title: listing.title,
        annual_income: parseFloat(fin.annual_income),
        monthly_debts: parseFloat(fin.monthly_debts) || 0,
        down_payment: parseFloat(fin.down_payment) || 0,
        condition: vehicle.condition,
        fuel_type: vehicle.fuel_type,
        imported: vehicle.imported,
        employment_status: fin.employment_status,
      });
      setProReport(res.data);
      toast.success("Premium car report unlocked!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report. Please contact support.");
    } finally {
      setUnlockingReport(false);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    const txId = paymentData?.transaction_id || paymentData?.id || "UNKNOWN";
    await fetchProReport(txId);
  };

  const emailReport = async () => {
    try {
      await axios.post(`${API}/api/buying-power/car/email-pro-report`, {
        email: contact.email, name: contact.name, report: proReport,
      });
      toast.success("Report emailed — check your inbox!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email right now.");
    }
  };

  // Visual helper for match status
  const StatusBadge = ({ status }) => {
    const map = {
      strong: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: CheckCircle2, label: "You Qualify" },
      possible: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: AlertTriangle, label: "Possible — Tight" },
      not_yet: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", icon: XCircle, label: "Not Yet" },
    };
    const s = map[status] || map.possible;
    const Icon = s.icon;
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${s.bg} ${s.text} ${s.border} text-sm font-semibold`}>
        <Icon className="w-4 h-4" /> {s.label}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fffe] via-white to-[#f0fafa] pt-24 pb-16 px-4">
      <div className={`${step === 4 && proReport ? "max-w-2xl lg:max-w-6xl" : "max-w-2xl"} mx-auto`}>

        {/* Back + progress */}
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#3E9690] mb-4 transition-colors"
          data-testid="car-back-to-mode"
        >
          <ArrowLeft className="w-4 h-4" /> Back to choice
        </button>

        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Step {step} of {totalSteps} · Car Buying Power</span>
            <span>{Math.round(pct)}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#52BFB8] to-[#3E9690] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-10" data-testid="car-buying-power-card">

          {/* STEP 1 — Upload listing screenshot + manual price */}
          {step === 1 && (
            <div className="space-y-5" data-testid="car-step-1">
              <div className="text-center mb-2">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#3E9690] to-[#2A7873] flex items-center justify-center mb-4">
                  <Car className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-[#2D3748] mb-2 font-cabinet">Which car are you eyeing?</h1>
                <p className="text-gray-500 text-sm">Snap a screenshot of any listing — we'll read the price automatically.</p>
              </div>

              {scrapeError && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-sm text-amber-800" data-testid="car-scrape-error">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{scrapeError}</span>
                </div>
              )}

              {/* Screenshot upload */}
              <label className="block cursor-pointer" data-testid="car-screenshot-upload-label">
                <div className={`border-2 border-dashed ${ocrScanning ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-300 hover:border-[#52BFB8] hover:bg-[#52BFB8]/5"} rounded-2xl p-6 text-center transition-all`}>
                  {ocrScanning ? (
                    <>
                      <Loader2 className="w-8 h-8 mx-auto text-[#52BFB8] animate-spin mb-2" />
                      <p className="text-sm font-semibold text-[#3E9690]">Reading your screenshot…</p>
                    </>
                  ) : listing.image ? (
                    <>
                      <img src={listing.image} alt="listing" className="max-h-32 mx-auto rounded-lg mb-2 object-cover" />
                      <p className="text-xs text-gray-500">Click to replace</p>
                    </>
                  ) : (
                    <>
                      <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm font-semibold text-gray-700">Upload a listing screenshot</p>
                      <p className="text-xs text-gray-500 mt-1">PNG / JPG · max 6MB</p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="car-screenshot-input"
                    onChange={(e) => importFromScreenshot(e.target.files?.[0])}
                  />
                </div>
              </label>

              {/* Site hints */}
              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-600">
                <p className="font-semibold mb-1.5 text-gray-700">Popular Bahamian car sites:</p>
                <div className="flex flex-wrap gap-1.5">
                  {CAR_SITES.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2.5 py-1 bg-white border border-gray-200 rounded-full hover:border-[#52BFB8] hover:text-[#3E9690] transition-colors"
                      data-testid={`car-site-${s.name.toLowerCase().replace(/ /g, "-")}`}
                    >
                      {s.name} →
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 uppercase tracking-wider">or enter manually</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Manual price */}
              <div className="space-y-3">
                <label className="block">
                  <span className="text-sm font-semibold text-gray-700 block mb-1.5">Vehicle title (optional)</span>
                  <Input
                    type="text"
                    placeholder="e.g. 2022 Honda CR-V"
                    value={listing.title}
                    onChange={(e) => setListing((l) => ({ ...l, title: e.target.value }))}
                    data-testid="car-title-input"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-gray-700 block mb-1.5">Asking price (BSD / USD)</span>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      placeholder="e.g. 25000"
                      value={listing.price}
                      onChange={(e) => setListing((l) => ({ ...l, price: e.target.value }))}
                      className="pl-10"
                      data-testid="car-price-input"
                    />
                  </div>
                </label>
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!listing.price || parseFloat(listing.price) <= 0}
                className="btn-primary w-full"
                data-testid="car-next-step-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* STEP 2 — Vehicle details (new/used, fuel, imported) */}
          {step === 2 && (
            <div className="space-y-6" data-testid="car-step-2">
              {listing.price && (
                <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                  {listing.image && <img src={listing.image} alt="car" className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">You're checking</p>
                    <p className="text-sm font-semibold truncate">{listing.title || "This vehicle"} — {fmt(listing.price)}</p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <h2 className="text-2xl font-bold text-[#2D3748] font-cabinet mb-1">Tell us about this car</h2>
                <p className="text-sm text-gray-500">A few details so we can price it right for you.</p>
              </div>

              {/* Condition */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Condition</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { k: "new", label: "New", help: "Dealer-fresh, 0km" },
                    { k: "used", label: "Used", help: "Pre-owned" },
                  ].map((o) => (
                    <button
                      key={o.k}
                      onClick={() => setVehicle((v) => ({ ...v, condition: o.k }))}
                      data-testid={`car-condition-${o.k}`}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${vehicle.condition === o.k ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-200 hover:border-gray-300"}`}
                    >
                      <div className="text-sm font-semibold text-[#2D3748]">{o.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{o.help}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fuel type */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Fuel type</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { k: "gas", label: "Gas", icon: Fuel },
                    { k: "hybrid", label: "Hybrid", icon: Fuel },
                    { k: "electric", label: "Electric", icon: Zap },
                  ].map((o) => {
                    const Icon = o.icon;
                    return (
                      <button
                        key={o.k}
                        onClick={() => setVehicle((v) => ({ ...v, fuel_type: o.k }))}
                        data-testid={`car-fuel-${o.k}`}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${vehicle.fuel_type === o.k ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <Icon className="w-5 h-5 text-[#3E9690]" />
                        <div className="text-sm font-semibold text-[#2D3748]">{o.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Imported or local */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Where is this car coming from?</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setVehicle((v) => ({ ...v, imported: false }))}
                    data-testid="car-local-btn"
                    className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${!vehicle.imported ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <Flag className="w-5 h-5 text-[#3E9690]" />
                    <div className="text-left">
                      <div className="text-sm font-semibold text-[#2D3748]">Local dealer</div>
                      <div className="text-xs text-gray-500">All taxes included</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setVehicle((v) => ({ ...v, imported: true }))}
                    data-testid="car-imported-btn"
                    className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${vehicle.imported ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <Ship className="w-5 h-5 text-[#3E9690]" />
                    <div className="text-left">
                      <div className="text-sm font-semibold text-[#2D3748]">Imported</div>
                      <div className="text-xs text-gray-500">Duty + freight added</div>
                    </div>
                  </button>
                </div>
                {vehicle.imported && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      We'll add Bahamas customs duty, 10% VAT, ~$500 in fees, and $1,200 freight estimate. These are averages — your freight forwarder gives the firm quote.
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="car-back-step-2">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button onClick={() => setStep(3)} className="btn-primary flex-1" data-testid="car-next-step-2">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3 — Income + debts + down payment */}
          {step === 3 && (
            <div className="space-y-5" data-testid="car-step-3">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-[#2D3748] font-cabinet mb-1">Your finances</h2>
                <p className="text-sm text-gray-500">We only check the ratios lenders care about — nothing is shared.</p>
              </div>

              <label className="block">
                <span className="text-sm font-semibold text-gray-700 block mb-1.5">Annual income (BSD)</span>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="e.g. 60000"
                    value={fin.annual_income}
                    onChange={(e) => setFin({ ...fin, annual_income: e.target.value })}
                    className="pl-10"
                    data-testid="car-income-input"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-gray-700 block mb-1.5">Monthly debt payments</span>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="Credit cards + existing loans"
                    value={fin.monthly_debts}
                    onChange={(e) => setFin({ ...fin, monthly_debts: e.target.value })}
                    className="pl-10"
                    data-testid="car-debts-input"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-gray-700 block mb-1.5">Down payment (optional)</span>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="e.g. 2500"
                    value={fin.down_payment}
                    onChange={(e) => setFin({ ...fin, down_payment: e.target.value })}
                    className="pl-10"
                    data-testid="car-dp-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Most Bahamian banks finance 100% of a new car — but a down payment lowers your monthly.</p>
              </label>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Employment</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { k: "employed", label: "Employed" },
                    { k: "self_employed", label: "Self-employed" },
                  ].map((o) => (
                    <button
                      key={o.k}
                      onClick={() => setFin((f) => ({ ...f, employment_status: o.k }))}
                      data-testid={`car-emp-${o.k}`}
                      className={`p-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${fin.employment_status === o.k ? "border-[#52BFB8] bg-[#52BFB8]/5 text-[#3E9690]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-5 space-y-3" data-testid="car-contact-section">
                <p className="text-sm font-semibold text-gray-700">Where should we send your results?</p>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="text" placeholder="Full name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="pl-10" data-testid="car-gate-name" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="email" placeholder="Email address (required)" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="pl-10" data-testid="car-gate-email" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="tel" placeholder="Phone (optional)" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="pl-10" data-testid="car-gate-phone" />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="car-back-step-3">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  onClick={calculate}
                  disabled={!fin.annual_income || parseFloat(fin.annual_income) <= 0 || !contact.name.trim() || !EMAIL_RE.test(contact.email) || calculating}
                  className="btn-primary flex-1"
                  data-testid="car-calculate-btn"
                >
                  {calculating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculating…</> : <>See my range <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 4 — Free result + paywall OR Pro report */}
          {step === 4 && result && (
            <div className="space-y-6" data-testid="car-step-4">
              {!proReport ? (
                <>
                  {/* Free result */}
                  <div className="text-center">
                    <div className="inline-block mb-3">
                      <StatusBadge status={result.match_status} />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#2D3748] font-cabinet mb-2">
                      Your car price range
                    </h2>
                    <p className="text-sm text-gray-600 max-w-md mx-auto">{result.match_message}</p>
                  </div>

                  <BuyingPowerGauge
                    low={result.max_car_price_low}
                    high={result.max_car_price_high}
                    listingPrice={result.landed_total || parseFloat(listing.price) || 0}
                    matchStatus={result.match_status}
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Est. monthly</p>
                      <p className="text-xl font-bold text-[#2D3748]">
                        {fmt(result.est_monthly_payment_low)} – {fmt(result.est_monthly_payment_high)}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1" data-testid="car-monthly-range-note">Longest term → 3-year payoff</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Current TDSR</p>
                      <p className="text-xl font-bold text-[#2D3748]">{result.tdsr_pct}%</p>
                    </div>
                  </div>

                  {result.imported && (
                    <p className="text-xs text-center text-gray-500 -mt-3" data-testid="car-landed-note">
                      Gauge compares your range to the est. landed cost of {fmt(result.landed_total)}, not just the sticker price.
                    </p>
                  )}

                  {result.imported && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 flex items-start gap-2">
                      <Package className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Customs duty at <strong>{result.duty_pct_applied}%</strong> was factored in. The full landed-cost breakdown (freight, VAT, levies) unlocks in the Pro Report.
                      </span>
                    </div>
                  )}

                  {/* Paywall */}
                  <div className="bg-gradient-to-br from-[#52BFB8]/8 to-transparent border border-[#52BFB8]/30 rounded-2xl p-5" data-testid="car-paywall-section">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-5 h-5 text-[#3E9690]" />
                      <h3 className="font-bold text-[#2D3748]">Unlock the full Car Pro Report — $29.99</h3>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-1.5 mb-4 pl-1">
                      <li>• Per-bank interest rates, max term, and estimated monthly</li>
                      <li>• Which Bahamian lenders will actually approve you{result.imported ? " + full landed-cost breakdown" : ""}</li>
                      <li>• Emailable copy of your report</li>
                      <li>• Priority list of banks to approach first</li>
                    </ul>

                    {!showPayment ? (
                      <>
                        <p className="text-xs text-gray-500 mb-3" data-testid="car-paywall-email">Report will be linked to <strong>{contact.email}</strong></p>

                        <div className="mb-4">
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="Promo code (optional)"
                              value={promo.code}
                              onChange={(e) => setPromo((p) => ({ ...p, code: e.target.value.toUpperCase(), error: "", result: null }))}
                              className="flex-1 h-10"
                              data-testid="car-promo-input"
                            />
                            <Button
                              variant="outline"
                              onClick={applyPromo}
                              disabled={promo.applying || !promo.code.trim()}
                              className="h-10 whitespace-nowrap"
                              data-testid="car-promo-apply"
                            >
                              {promo.applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                            </Button>
                          </div>
                          {promo.error && <p className="text-xs text-rose-500 mt-1">{promo.error}</p>}
                          {promo.result && (
                            <p className="text-xs text-[#3E9690] mt-1">
                              ✓ {promo.result.description || `$${promo.result.discount_amount.toFixed(2)} off applied`}
                            </p>
                          )}
                        </div>

                        {promo.result?.is_free ? (
                          <Button onClick={unlockWithPromo} disabled={unlockingReport} className="btn-primary w-full" data-testid="car-unlock-free">
                            {unlockingReport ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Unlocking…</> : <><Sparkles className="w-4 h-4 mr-2" />Unlock My Free Car Report</>}
                          </Button>
                        ) : (
                          <Button onClick={startCheckout} disabled={gateSubmitting} className="btn-primary w-full" data-testid="car-unlock-btn">
                            {gateSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing checkout…</> : <><Lock className="w-4 h-4 mr-2" />Get my Car Pro Report</>}
                          </Button>
                        )}

                        <p className="text-xs text-gray-400 text-center mt-3">
                          Secure payment via PayPal · No subscription · Separate purchase from Property Pro Report
                        </p>
                      </>
                    ) : (
                      <div className="space-y-3" data-testid="car-paypal-section">
                        {unlockingReport ? (
                          <div className="flex items-center justify-center gap-2 py-6 text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin" /> Unlocking your car report…
                          </div>
                        ) : (
                          <PayPalButton
                            amount={29.99}
                            onSuccess={handlePaymentSuccess}
                            onError={() => toast.error("Payment failed. Please try again.")}
                          />
                        )}
                        <button
                          onClick={() => setShowPayment(false)}
                          className="w-full text-sm text-gray-500 hover:text-gray-700"
                          data-testid="car-cancel-payment"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <CarProReportView
                  report={proReport}
                  onEmail={emailReport}
                  contact={contact}
                  listingTitle={listing.title}
                  financed={Math.max(0, (proReport.landed_cost?.landed_total || 0) - (parseFloat(fin.down_payment) || 0))}
                  onFullApp={() => {
                  sessionStorage.setItem("buyingPowerPrefill", JSON.stringify({
                    gross_monthly_salary: parseFloat(fin.annual_income) / 12,
                    credit_card_payment: parseFloat(fin.monthly_debts) || 0,
                    email: contact.email, phone: contact.phone, name: contact.name,
                  }));
                  navigate("/qualify");
                }} />
              )}

              {!proReport && <p className="text-xs text-gray-400 text-center">{result.disclaimer}</p>}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}


function CarProReportView({ report, onEmail, contact, onFullApp, financed, listingTitle }) {
  const landed = report.landed_cost || {};
  const [emailing, setEmailing] = useState(false);
  const [term, setTerm] = useState(Math.max(...(report.bank_rows || []).map((r) => r.term_years), 3));
  const rows = bankRowsAtTerm(report.bank_rows, financed, term);
  const handleEmail = async () => {
    setEmailing(true);
    try { await onEmail(); } finally { setEmailing(false); }
  };

  return (
    <div className="space-y-6" data-testid="car-pro-report">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#52BFB8] via-[#3E9690] to-[#2A7873] rounded-3xl p-6 sm:p-8 text-white text-center shadow-xl">
        <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-3">
          <Crown className="w-3.5 h-3.5" /> Car Pro Report
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold font-cabinet mb-2">{report.headline}</h2>
        <p className="text-white/90 text-sm max-w-xl mx-auto">{report.summary}</p>
      </div>

      {/* Range + monthly */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#52BFB8]/10 to-transparent border border-[#52BFB8]/20 rounded-2xl p-5 text-center lg:col-span-1">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Your car price range</p>
          <p className="text-2xl font-bold text-[#2D3748]">
            ${Math.round(report.max_car_price_low).toLocaleString()} – ${Math.round(report.max_car_price_high).toLocaleString()}
          </p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-5 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Est. monthly payment</p>
          <p className="text-2xl font-bold text-[#2D3748]">
            ${Math.round(report.est_monthly_payment_low).toLocaleString()} – ${Math.round(report.est_monthly_payment_high).toLocaleString()}
          </p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-5 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Current TDSR</p>
          <p className="text-2xl font-bold text-[#2D3748]">{report.tdsr_pct}%</p>
        </div>
      </div>

      {/* Itemized landed cost (if imported) */}
      {landed.imported && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="car-landed-cost">
          <h3 className="font-bold text-[#2D3748] mb-3 flex items-center gap-2">
            <Ship className="w-4 h-4 text-[#3E9690]" />
            Itemized landed cost (estimate)
          </h3>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-gray-100"><td className="py-2 text-gray-600">Listing price</td><td className="py-2 text-right font-semibold">${Math.round(landed.listing_price).toLocaleString()}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-2 text-gray-600">Customs duty ({landed.customs_duty_pct}%)</td><td className="py-2 text-right font-semibold">${Math.round(landed.customs_duty).toLocaleString()}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-2 text-gray-600">VAT (10%)</td><td className="py-2 text-right font-semibold">${Math.round(landed.vat).toLocaleString()}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-2 text-gray-600">Environmental levy</td><td className="py-2 text-right font-semibold">${Math.round(landed.env_levy).toLocaleString()}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-2 text-gray-600">Processing + stamp</td><td className="py-2 text-right font-semibold">${Math.round(landed.processing_stamp).toLocaleString()}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-2 text-gray-600">Freight (estimate)</td><td className="py-2 text-right font-semibold">${Math.round(landed.freight).toLocaleString()}</td></tr>
              <tr className="bg-[#52BFB8]/10"><td className="py-2.5 px-2 font-bold text-[#2D3748]">Total landed cost</td><td className="py-2.5 px-2 text-right font-bold text-[#3E9690]">${Math.round(landed.landed_total).toLocaleString()}</td></tr>
            </tbody>
          </table>
          <p className="text-[11px] text-gray-500 mt-2 italic">
            Freight, levy and processing are averages. Confirm the firm all-in quote with your freight forwarder before closing.
          </p>
        </div>
      )}

      {financed > 0 && <CarTermSlider term={term} onChange={setTerm} rows={rows} />}

      {/* Lender playbook */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="car-lender-playbook">
        <h3 className="font-bold text-[#2D3748] mb-3">Lender playbook — who to approach first</h3>
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.bank_id} className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${row.qualifies ? "bg-emerald-50/50 border-emerald-200" : "bg-amber-50/50 border-amber-200"}`}>
              <div className="flex items-center gap-3 min-w-0">
                {row.qualifies ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />}
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#2D3748] truncate">{row.bank_name}</p>
                  <p className="text-xs text-gray-500">{row.rate}% · up to {row.term_years} yrs · max loan ${Math.round(row.max_loan).toLocaleString()}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  Est. monthly ({row.chosen_years} yrs)
                  {row.capped && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold" data-testid={`car-bank-capped-${row.bank_id}`}>max {row.term_years} yrs</span>}
                </p>
                <p className="font-bold text-[#3E9690]" data-testid={`car-bank-monthly-${row.bank_id}`}>${Math.round(row.chosen_monthly).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Next steps */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h3 className="font-bold text-[#2D3748] mb-3">Next steps</h3>
        <ul className="space-y-2">
          {(report.next_steps || []).map((s, i) => (
            <li key={i} className="flex gap-2 text-sm text-gray-700">
              <ArrowRight className="w-4 h-4 text-[#3E9690] flex-shrink-0 mt-0.5" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button onClick={onFullApp} className="btn-primary flex-1" data-testid="car-start-full-app">
          Start my full loan pre-qualification <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button variant="outline" onClick={handleEmail} disabled={emailing} className="flex-1" data-testid="car-email-report">
          {emailing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Mail className="w-4 h-4 mr-2" />Email me this report</>}
        </Button>
        <Button
          variant="outline"
          onClick={() => downloadCarReportPdf({ report, contact, listingTitle, financed, termYears: term })}
          className="flex-1"
          data-testid="car-download-pdf"
        >
          <Download className="w-4 h-4 mr-2" />Download PDF
        </Button>
      </div>

      <p className="text-[11px] text-gray-400 text-center italic">{report.disclaimer}</p>
    </div>
  );
}
